import { useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";

import PathMap from "./PathMap";
import { HISTORY_POP, MAX_MAP_SIZE } from "./constants";
import { isServer } from "../environmentDetection/library";

/**
 * The problem with SPA's is that it's difficult for the browser to maintain
 * a history of scroll positions. This function should exist at the highest
 * level possible. This will listen for changes in the location from React Router,
 * and on "POP" actions (user clicking back), it will check an internal map to determine
 * what the previously recorded scroll position was, and scrolls the SPA to the Y offset
 *
 * @returns {null}
 */
export const ScrollRestore = () => {
    const { pathname } = useLocation();
    const { action } = useHistory();

    useEffect(() => {
        if (action === HISTORY_POP) {
            const scrollAmount = PathMap.get(pathname);

            if (scrollAmount) {
                scrollTo(scrollAmount);
                PathMap.delete(pathname);
            }
        } else {
            window.scrollTo(0, 0);
        }
    }, [pathname]);

    return null;
};

/**
 * This function sets the last recorded scroll position on a page
 * before navigating
 *
 * @param {string} path
 * @returns {void}
 */
export const setPageScrollPosition = (path) => {
    if (isServer()) {
        return;
    }
    dequeuePaths();
    const amount = getScrollPosition();

    PathMap.set(path, amount);
};

/**
 * This function handles cleaning up paths once the map has
 * exceeded the determined max size
 *
 * @returns {void}
 */
export const dequeuePaths = () => {
    if (PathMap.size > MAX_MAP_SIZE) {
        const [key] = PathMap.entries().next().value;

        PathMap.delete(key);
    }
};

/**
 * This function grabs the current page Y offset position
 *
 * @returns {number} the current scroll position
 */
export const getScrollPosition = () => {
    if (isServer()) {
        return;
    }
    return window.scrollY;
};

/**
 * This function scrolls to the specified offet
 *
 * @param {number} [offset=0] the page Y offset to scroll to
 * @returns {void}
 */
export const scrollTo = (offset = 0) => {
    if (isServer()) {
        return;
    }

    // Right now we need to wait for content so we offset with a timeout
    setTimeout(() => {
        window.scrollTo(0, offset);
    }, 1000);
};
